import DOMPurify from 'dompurify';

// Alert bodies are HTML: `ic3_alerts.full_text` (and `hot_scams.what_to_know`)
// hold the admin editor's output, sanitized on the server at save time by
// backend/utils/alertBodySanitizer.js. That file is the authority on what a
// body may contain; this one mirrors it for the editor's palette and for a
// second sanitizing pass at render time. (The markdown subset these fields
// used to be written in was converted and retired in backend migration 024.)

// The only text colours an alert can use. Keep in step with COLOR_PRESETS in
// backend/utils/alertBodySanitizer.js: a colour missing there is stripped on save.
export const COLOR_PRESETS = [
  { name: 'Red', hex: '#B02418', hint: 'Warnings and scam verdicts' },
  { name: 'Orange', hex: '#C2571A', hint: 'Caution' },
  { name: 'Blue', hex: '#1B5A8F', hint: 'Agency names and references' },
  { name: 'Green', hex: '#2E7D32', hint: 'Safe / what to do' },
  { name: 'Gray', hex: '#6B7280', hint: 'Notes and fine print' },
];

const ALLOWED_TAGS = [
  'p', 'h2', 'h3', 'ul', 'ol', 'li', 'strong', 'em', 'u', 's',
  'a', 'span', 'br', 'blockquote', 'hr',
  // Grids (10-28): the editor's table block; data-style picks tiles / grid.
  'table', 'thead', 'tbody', 'tr', 'th', 'td', 'colgroup', 'col',
];

const hexToRgb = (hex) => {
  const n = parseInt(hex.slice(1), 16);
  return `rgb(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255})`;
};

// Normalized (lower-case, no spaces) forms of every preset, hex and rgb().
const ALLOWED_COLORS = new Set(
  COLOR_PRESETS.flatMap(({ hex }) => [hex, hexToRgb(hex)]).map((c) => c.toLowerCase().replace(/\s+/g, ''))
);

const presetColorOf = (style) => {
  const m = /^\s*color\s*:\s*([^;]+?)\s*;?\s*$/i.exec(style || '');
  if (!m) return null;
  return ALLOWED_COLORS.has(m[1].toLowerCase().replace(/\s+/g, '')) ? m[1] : null;
};

// Hooks are global to DOMPurify; this module is its only user in the app.
DOMPurify.addHook('uponSanitizeAttribute', (node, data) => {
  if (data.attrName !== 'style') return;
  const color = node.nodeName === 'SPAN' ? presetColorOf(data.attrValue) : null;
  if (color) data.attrValue = `color: ${color}`;
  else data.keepAttr = false;
});

DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  // A link that lost its href, or a span that lost its colour, carries
  // nothing: unwrap it so the text does not render as a dead link.
  const hollow = (node.nodeName === 'A' && !node.getAttribute('href'))
    || (node.nodeName === 'SPAN' && !node.getAttribute('style'));
  if (hollow && node.parentNode) {
    node.replaceWith(...node.childNodes);
    return;
  }
  if (node.nodeName === 'A') {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer nofollow');
  }
});

/** Sanitize an HTML alert body for rendering. The server already did; this is the second guard. */
export const sanitizeAlertHtml = (html) =>
  DOMPurify.sanitize(html || '', {
    ALLOWED_TAGS,
    ALLOWED_ATTR: ['href', 'target', 'rel', 'style', 'data-style', 'data-lines', 'colspan', 'rowspan'],
    ALLOWED_URI_REGEXP: /^(?:https?:|mailto:|tel:)/i,
  });

/**
 * A colour as the editor reports it ('#B02418', or 'rgb(176, 36, 24)' once the
 * browser has re-serialized it) -> the matching preset's hex, lower-case, or ''.
 */
export const presetHexOf = (color) => {
  const norm = (color || '').toLowerCase().replace(/\s+/g, '');
  if (!norm) return '';
  const hit = COLOR_PRESETS.find(
    ({ hex }) => hex.toLowerCase() === norm || hexToRgb(hex).replace(/\s+/g, '') === norm
  );
  return hit ? hit.hex.toLowerCase() : '';
};

/** Drop the empty paragraphs an editor leaves at the end of a document. */
export const trimTrailingEmpty = (html) =>
  (html || '').replace(/(?:<p>(?:\s|&nbsp;|<br[^>]*>)*<\/p>\s*)+$/i, '');

/** True when an HTML body has no readable text (an empty editor is "<p></p>"). */
export const isEmptyHtml = (html) => {
  if (!html) return true;
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim() === '';
};

/**
 * Hot scam list sections (02-06) are arrays of inline-HTML items but are
 * authored in the block editor, one item per paragraph or bullet. These two
 * convert between the shapes. Block elements the editor can produce (p, li,
 * h2, h3, blockquote) each become one item; nested lists flatten.
 */
export const itemsToHtml = (items) =>
  (Array.isArray(items) ? items : []).map((i) => `<p>${i}</p>`).join('');

export const htmlToItems = (html) => {
  if (!html || typeof DOMParser === 'undefined') return [];
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  const items = [];
  const walk = (node) => {
    for (const el of node.children) {
      if (/^(UL|OL)$/.test(el.tagName)) { walk(el); continue; }
      if (el.tagName === 'LI') {
        // An li wraps its text in a p; nested lists sit beside it.
        const nested = [...el.children].filter((c) => /^(UL|OL)$/.test(c.tagName));
        nested.forEach((n) => n.remove());
        const inner = el.children.length === 1 && el.children[0].tagName === 'P'
          ? el.children[0].innerHTML : el.innerHTML;
        if (!isEmptyHtml(inner)) items.push(inner.trim());
        nested.forEach(walk);
        continue;
      }
      if (/^(P|H2|H3|BLOCKQUOTE|DIV)$/.test(el.tagName)) {
        if (!isEmptyHtml(el.innerHTML)) items.push(el.innerHTML.trim());
      }
    }
  };
  walk(doc.body);
  return items;
};

// Small builders for writing HTML bodies in code (the per-agency templates),
// producing the same shapes the editor does. Text is taken as trusted literal
// source text, not user input.
export const para = (text) => `<p>${text}</p>`;
export const heading = (text) => `<h2>${text}</h2>`;
export const bullets = (...items) => `<ul>${items.map((i) => `<li><p>${i}</p></li>`).join('')}</ul>`;
